import _ from 'lodash';
import type { QuillOptions } from 'quill';
import { useEffect, useRef, useState } from 'react';
import { useQuill } from 'react-quilljs';
import { getElementComputedHeight } from '../../helpers/window';

export type QuillInsertOp = {
  insert?: string | object;
};

/**
 * Parse a JSON string of Delta insert operations or return a default empty value if the passed
 */
export const parseContentsJSON = (contents: string): QuillInsertOp[] => {
  try {
    const ops = JSON.parse(contents);
    if (Array.isArray(ops)) {
      return ops as QuillInsertOp[];
    }
  } catch (_e) {
    // Carry on.
  }

  // If contents is a string that is not empty, assume that it's plain text.
  if (_.isString(contents) && contents) {
    return [
      { insert: contents },
      // The last insert op must be a newline.
      { insert: '\n' },
    ];
  }

  return [];
};

export type QuillEditorProps = {
  totalHeight: number;
  initialContents: QuillInsertOp[];
  quillOptions?: QuillOptions;
  maxLength?: number;
  onChange: (state: { contents: QuillInsertOp[]; length: number }) => void;
};

/**
 * Display a Quill editor with a specified total height.
 */
const QuillEditor = ({
  totalHeight,
  initialContents,
  quillOptions,
  maxLength,
  onChange,
}: QuillEditorProps) => {
  const { quill, quillRef } = useQuill(quillOptions);

  const toolbarRef = useRef<HTMLElement>();

  // The default toolbar with one row of controls has an overall height of about 42px.
  const [toolbarHeight, setToolbarHeight] = useState<number>(42);

  useEffect(() => {
    if (quill) {
      const toolbar = quill.getModule('toolbar');
      if (toolbar !== toolbarRef.current) {
        toolbarRef.current = toolbar;
        setToolbarHeight(getElementComputedHeight(toolbar.container));
      }

      quill.setContents(initialContents);

      // Compute the initial character count.
      onChange({
        contents: initialContents,
        length: quill.getLength() - 1,
      });

      quill.on('text-change', () => {
        // Quill always places a newline character at the end. Do not count it as part of the length.
        const length = quill.getLength() - 1;
        //  Handle max length
        if (maxLength && length > maxLength) {
          quill.deleteText(maxLength, length);
        } else {
          onChange({
            contents: quill.getContents().ops,
            length,
          });
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quill]);

  return (
    <div style={{ height: `${totalHeight}px` }}>
      <div style={{ height: `${totalHeight - toolbarHeight}px` }} ref={quillRef} />
    </div>
  );
};

export default QuillEditor;
